// Persist the currently-simulated talent id across refreshes, so a reload keeps
// the tester acting as the same talent (mirrors web's concierge-session.ts).
// Defensive try/catch: localStorage can throw in private mode / sandboxed iframes.

import { SEED_SELLER_ID } from "../config";
import { projectFilterFromSearch } from "./project-filter";
import { pathToRoute } from "./route-url";

const KEY = "recruiter_sim_seller_id";

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The effective talent id at startup: a stored choice, else the seeded env id. */
export function initialSellerId(): string {
  const s = storage();
  if (s) {
    try {
      const v = s.getItem(KEY);
      if (v) return v;
    } catch {
      /* ignore */
    }
  }
  return SEED_SELLER_ID;
}

/** Pure boot decision (no window / storage access), so it's node-testable. Given
 *  the URL and the persisted talent id, decide the opening (sellerId, threadId):
 *  a talent PATH route wins (deep link / reload of a canonical path); else a
 *  `?project=` scope link — the client debug "Talent platform" button — FORCES the
 *  directory (empty id) so the project-filtered picker shows instead of the
 *  persisted talent's inbox; else the persisted id (or the env seed). */
export function resolveBootSession(
  pathname: string,
  search: string,
  persistedSellerId: string,
): { sellerId: string; threadId: string | null } {
  const route = pathToRoute(pathname);
  if (route && route.kind !== "directory") {
    return {
      sellerId: route.sellerId,
      threadId: route.kind === "thread" ? route.threadId : null,
    };
  }
  if (projectFilterFromSearch(search)) {
    return { sellerId: "", threadId: null };
  }
  return { sellerId: persistedSellerId, threadId: null };
}

/** Boot-time session state: delegates the decision to resolveBootSession, then
 *  persists a URL-named talent identity so localStorage mirrors the URL (only for
 *  a real talent PATH route — a `?project=` scope link deliberately shows the
 *  picker WITHOUT touching the persisted id). The `typeof window` guard keeps the
 *  store importable under node (vitest). */
export function initialSessionState(): { sellerId: string; threadId: string | null } {
  if (typeof window !== "undefined") {
    const boot = resolveBootSession(
      window.location.pathname,
      window.location.search,
      initialSellerId(),
    );
    const route = pathToRoute(window.location.pathname);
    if (route && route.kind !== "directory") {
      storeSellerId(boot.sellerId);
    }
    return boot;
  }
  return { sellerId: initialSellerId(), threadId: null };
}

export function storeSellerId(sellerId: string): void {
  const s = storage();
  if (!s) return;
  try {
    if (sellerId) s.setItem(KEY, sellerId);
    else s.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
